import Script from 'next/script'
import { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { cookieConsentSelectors } from '@/store/cookieConsent'

const thirdPartyScripts = [
  {
    id: 'google-analytics',
    Component: ({ id, nonce, params }) => (
      <Script
        id={id}
        key={id}
        src={`https://www.googletagmanager.com/gtag/js?id=${params.googleAnalyticsId}`}
        strategy="afterInteractive"
        nonce={nonce}
      />
    ),
    condition: params => !!params.googleAnalyticsId
  },
  {
    id: 'gtag-init',
    Component: ({ id, nonce, params }) => (
      <Script id={id} strategy="afterInteractive" nonce={nonce}>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${params.googleAnalyticsId}', {
            page_path: window.location.pathname,
          });
        `}
      </Script>
    ),
    condition: params => !!params.googleAnalyticsId
  },
  {
    id: 'cookie-yes-settings',
    Component: ({ id, nonce, params }) => (
      <Script id={id} strategy="afterInteractive" nonce={nonce}>
        {`
          (function() {
            window.COOKIE_YES_CLIENT_ID = '${params.cookieYesClientId}'
            function initializeCkySettings() {
              const userLang = localStorage.getItem('locale') || 'en';

              window.ckySettings = {
                documentLang: userLang
              };
            }

            initializeCkySettings();
          })();
        `}
      </Script>
    ),
    condition: params => !!params.cookieYesClientId
  },
  {
    id: 'cookie-yes',
    Component: ({ id, nonce, params }) => (
      <Script
        src={`https://cdn-cookieyes.com/client_data/${params.cookieYesClientId}/script.js`}
        id={id}
        key={id}
        nonce={nonce}
        strategy="afterInteractive"
      ></Script>
    ),
    condition: params => !!params.cookieYesClientId
  }
]

export default function ThirdPartyScripts({ nonce, googleAnalyticsId, cookieYesClientId }) {
  const shouldLoadCookieYesScripts = useSelector(cookieConsentSelectors.selectShouldLoad)
  const getParamsByScriptId = useCallback(
    scriptId => {
      switch (scriptId) {
        case 'gtag-init':
        case 'google-analytics':
          return { googleAnalyticsId }
        case 'cookie-yes':
        case 'cookie-yes-settings':
          return { cookieYesClientId }
        default:
          return {}
      }
    },
    [cookieYesClientId, googleAnalyticsId]
  )

  const filteredScripts = useMemo(
    () =>
      thirdPartyScripts.filter(script => {
        if (script.id.startsWith('cookie-yes') && !shouldLoadCookieYesScripts) {
          return false
        }

        if (typeof script.condition === 'function') {
          return script.condition(getParamsByScriptId(script.id))
        }
        return script.condition
      }),
    [getParamsByScriptId, shouldLoadCookieYesScripts]
  )

  if (!filteredScripts.length) return null

  return (
    <>
      {filteredScripts.map(script => (
        <script.Component key={script.id} id={script.id} nonce={nonce} params={getParamsByScriptId(script.id)} />
      ))}
    </>
  )
}
